/**
 * PanelToggle — a card-style "show/hide this panel" pill with an icon + label.
 *
 * Replaces the hand-rolled toggle copy-pasted across the explorer pages
 * (ClickHouseExplorer's Tables/Search/Schema/Guide row, S3Explorer's Stats):
 *
 *   <button className="flex items-center gap-1.5 card px-3 py-2 ..."
 *           style={{ color: active ? var(--accent) : var(--text-secondary),
 *                    border: active ? '1px solid ...accent 40%...' : undefined }}>
 *     <span>{icon}</span><span className="font-medium">{label}</span>
 *   </button>
 *
 * Inactive renders as a neutral `card` (bg-secondary + border + shadow) in the
 * secondary text color; active tints the text/border with the accent. The
 * caller still owns the row layout (the flex + gap between toggles).
 *
 * CSS-var primitive (not Tailwind): mirrors the global `.card` look with the
 * theme tokens so it matches the surrounding cards exactly.
 */

import { forwardRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';

export interface PanelToggleProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Whether the panel this toggle controls is currently open. */
  active: boolean;
  /** Optional leading glyph/emoji shown before the label. */
  icon?: ReactNode;
}

export const PanelToggle = forwardRef<HTMLButtonElement, PanelToggleProps>(
  ({ active, icon, children, style, type = 'button', disabled, ...rest }, ref) => {
    const [hover, setHover] = useState(false);
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 12px',
          fontSize: 12,
          background: 'var(--bg-secondary)',
          color: active ? 'var(--accent)' : 'var(--text-secondary)',
          border: `1px solid ${active ? 'color-mix(in srgb, var(--accent) 40%, transparent)' : 'var(--border)'}`,
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : hover ? 0.8 : 1,
          transition: 'opacity 0.15s',
          ...style,
        }}
        {...rest}
      >
        {icon != null && <span style={{ lineHeight: 1 }}>{icon}</span>}
        <span style={{ fontWeight: 500 }}>{children}</span>
      </button>
    );
  },
);

PanelToggle.displayName = 'PanelToggle';
